import {
  useCallback,
  useEffect,
  useRef,
  type KeyboardEvent,
  type ReactElement,
} from 'react';
import type { BoardFaceList } from '@molt/shared';
import { Button, CardSurface, InlineEdit } from '@/components';
import { announce } from '@/lib/announce';
import { cardDisplayTitle } from '@/lib/card-title';
import { isOptimisticCardId, useArchiveCard, useRenameCard } from '@/lib/cards';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { useUndoRedo } from '@/lib/undo';

/**
 * Quick edit — `FE-12.2`, `FR-BOARD-022`. `docs/design/c3/C3-BRIEF.md` §6.6.
 *
 * ── It does not move the card ─────────────────────────────────────────────
 * AC1: "the card still in position". This renders **in place of** the tile's
 * content, inside the same `<li>` (`SortableCardTile`'s, agent E — this file
 * never renders one of its own), elevated — no portal, no backdrop, no
 * `Dialog`. That is what makes the criterion literally true rather than
 * approximately true. The root below is a `CardSurface` `div`, not an anchor:
 * it is a stand-in for the tile's `<a>`, and it must not itself be a link.
 *
 * ── `defaultEditing` does the "title selected" half of AC1 for free ───────
 * `InlineEdit` mounts with `defaultEditing` and its own open-effect calls
 * `.focus()` then `.select()` — nothing here has to. `InlineEdit.tsx` is not
 * this chunk's to change and is not touched.
 *
 * ── `multiline` is deliberately `false` here, unlike the composer ─────────
 * `CardComposer` (`FE-12.1`) wants a multi-line field because a card title is
 * a sentence, and its own `<form>` intercepts Enter itself. `InlineEdit`'s
 * shared state machine reads Enter differently depending on `multiline`:
 * multiline treats a plain Enter as a newline and requires Cmd/Ctrl+Enter to
 * commit; single-line treats plain Enter as commit. `FR-BOARD-022` AC2 is
 * explicit — "presses Enter" commits, no modifier — so this field is
 * single-line. The trade: a title that already contains a newline (typed via
 * the composer's Shift+Enter, or arriving from a paste) is edited here as
 * flattened text. No acceptance criterion in this chunk exercises that
 * combination; flagged for whoever next touches this file.
 *
 * ── `commitOn="enter"`, never `"both"` ─────────────────────────────────────
 * `ListHeader.tsx` documents why: `"both"` reads a programmatic blur as "no
 * change, close". Quick edit moves focus itself (Escape leaves the field for
 * good, a click can land on Archive), so a blur must do nothing. Enter
 * commits (AC2); Escape cancels via `onCancel` (AC3) — routed to `close()`
 * below, since a card's editor has nowhere to go idle back to the way a list
 * header's does; the whole panel closes with it.
 *
 * ── ⚠ Two of `InlineEdit.commit()`'s own branches never call `onCommit` ────
 * An unchanged submission (`next === value`) and an emptied one
 * (`reject-and-restore`) both `close()` **internally** and return WITHOUT
 * calling `onCommit` — so `handleTitleCommit` below never runs for either,
 * and relying on it alone would leave this panel mounted, showing
 * `InlineEdit`'s own idle button underneath (the exact label-folding shape
 * this file otherwise guards against). Unlike Escape, `InlineEdit` does not
 * `stopPropagation` on Enter, so the keydown still reaches the panel-level
 * handler below after `InlineEdit` is done with it — `handlePanelKeyDown`
 * closes on ANY Enter that reaches it, which is exactly the two cases above
 * (a real commit already closed the panel itself, making this a no-op via
 * `close()`'s own idempotency guard).
 *
 * ── The label-folding trap, in its second home ────────────────────────────
 * `InlineEdit`'s idle button appends an unconditional
 * `<span class="sr-only">, edit {label}</span>` — the panel's own name does
 * not depend on it either way: `role="group"` carries an explicit
 * `aria-label` computed from `card.title` alone, so nothing the two buttons,
 * the field, or (per the note above) a stray idle button can fold into it.
 *
 * ── Two actions, plain Buttons, not a Menu ─────────────────────────────────
 * `Open card` (the same anchor and `href` `CardTile` renders — the card back
 * is `FE-14.1`, chunk `E1`, and does not exist yet; this file does not fix
 * that) and `Archive card` (AC4, closes quick edit on success). The other
 * five actions `FR-BOARD-022`'s behaviour sentence lists — labels, members,
 * due date, move, copy — belong to later chunks (`E2`, `D4`, `B24`,
 * `FR-BOARD-026`, unowned respectively; brief §2.1 item 2). ⏭ FORWARD: once
 * those exist, two `Button`s in a stack stops scaling and a `Menu` is the
 * right call — it was rejected here only because two items do not need one.
 *
 * ── The fourth optimistic gate (brief §5.4) ───────────────────────────────
 * `CardTile` is expected to withhold `onQuickEdit` for a placeholder card, so
 * this component should never mount holding one. It checks anyway and closes
 * itself immediately rather than trusting that: a rename or archive here
 * would `PATCH`/`POST` an id the server has never seen. Hooks still run
 * unconditionally above the check (rules of hooks) — the guard only changes
 * what gets rendered and fires the close as a same-tick effect.
 *
 * ── `close()` is idempotent ────────────────────────────────────────────────
 * Four things can end this panel — a commit, an archive, Escape, and the
 * placeholder guard's effect — and, per the note above, Enter can now reach
 * the same panel-level handler a second time even after a commit already
 * closed it. `closedRef` makes every path collapse to exactly one call to
 * the caller's `onClose`, which is what every test in this file's suite that
 * counts calls is actually pinning.
 *
 * ── Announce: archive only, matching `ListHeader.tsx`'s own precedent ─────
 * Brief §5.5 puts rollback announcements in the mutation hooks
 * (`announceRollback` in `lib/cards.ts`, agent A's), never here — that part
 * is unambiguous. For the SUCCESS path the brief is silent, but C2 shipped a
 * call-site `announce('List <title> archived.')` for the equivalent list
 * action (`ListHeader.tsx`), and archiving is the one outcome here that
 * removes the card from the list entirely — invisible to a screen reader
 * the same way a list disappearing was. This file follows that precedent for
 * archive and does not add one for rename: the new title is read from the
 * card front itself (an `aria-label`, not a live region), the same way
 * `ListHeader.tsx` renames a list without announcing it either.
 *
 * ── ⚠ It also RECORDS AN UNDO SOURCE, and neither half of how is obvious ──
 * `FR-EXP-013`'s AC2 is "the user just archived a card … the toast's Undo".
 * Until this chunk only `BoardFace.tsx`'s `handleArchiveCardFront` — the `C`
 * shortcut on the card front — recorded one, so the same member archiving
 * through THIS panel got the announcement and no Undo at all.
 *
 * **`useUndoRedo` here rather than a `recordUndoSource` prop.** `BoardFace`
 * threads that prop down `SortableListColumn` → `ListColumn` → `ListHeader`;
 * it does not reach `SortableCardTile`, which is what mounts this panel. But
 * `lib/undo.ts` keeps its state in a module-scoped `Map` keyed by `boardId`
 * with a `window` `CustomEvent` fan-out, so a second hook instance on the same
 * board IS the board face's stack — its own suite pins that
 * ("synchronizes same-document hook instances"). `boardId` is already a prop.
 *
 * **`mutateAsync`, and NOT `mutate(vars, { onSuccess })`.** The obvious repair
 * is the one that silently does nothing. `close()` runs on the same tick and
 * the parent unmounts this panel; `@tanstack/query-core`'s `MutationObserver`
 * fires per-call `mutate` options only `if (this.#mutateOptions &&
 * this.hasListeners())`, and its `onUnsubscribe` has already detached the
 * observer from the mutation by then. The archive would still happen, the
 * announcement would still be made, and the undo source would still never be
 * recorded — with the repair "applied". `mutateAsync` returns
 * `Mutation.execute(variables)` straight through, which settles whether or not
 * anything is still mounted.
 *
 * `recordSource` itself is safe after unmount for the same reason: it mutates
 * module state and dispatches a `window` event, and the board face's own hook
 * instance is what re-renders.
 *
 * No error branch, deliberately: `useArchiveCard`'s `onError:
 * announceRollback(...)` is a MUTATION-level option, invoked by
 * `Mutation.execute` rather than through the observer, so the rollback still
 * speaks with this panel gone. The bare `.catch` exists only so a rejected
 * archive is not an unhandled rejection.
 */
export interface QuickEditProps {
  boardId: string;
  listId: string;
  card: BoardFaceList['cards'][number];
  onClose: () => void;
  onMove?: (() => void) | undefined;
  onCopy?: (() => void) | undefined;
}

export function QuickEdit({
  boardId,
  listId,
  card,
  onClose,
  onMove,
  onCopy,
}: QuickEditProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const renameCard = useRenameCard(boardId);
  const archiveCard = useArchiveCard(boardId);
  /*
   * Above the placeholder guard's early return, with the other hooks — see the
   * file header for why this is a hook and not a prop, and brief §5.4's note
   * that hooks run unconditionally above that check.
   */
  const { recordSource } = useUndoRedo(boardId, t);

  const closedRef = useRef(false);
  const close = useCallback(() => {
    if (closedRef.current) return;
    closedRef.current = true;
    onClose();
  }, [onClose]);

  // Brief §5.4's fourth site. See the file header.
  const isPlaceholder = isOptimisticCardId(card.id);

  useEffect(() => {
    if (isPlaceholder) close();
  }, [isPlaceholder, close]);

  if (isPlaceholder) return <></>;

  const title = cardDisplayTitle(t, card.title);
  const groupLabel = t('quickEdit.group', { title });

  // Both mutations are optimistic (agent A's `lib/cards.ts`), so the cache —
  // and therefore the card front — is already updated by the time `mutate`
  // returns. Closing right after is what makes AC2 and AC4's "…and closes"
  // true in the same tick as the edit, rather than waiting on a response.
  function handleTitleCommit(next: string) {
    renameCard.mutate({ cardId: card.id, listId, title: next });
    close();
  }

  function handleArchive() {
    /*
     * The announcement and registration now share the same SUCCESS phase.
     *
     * Announcing synchronously let the board face's optimistic neighbour move
     * speak after it, leaving the archive sentence visible for only a few
     * milliseconds. `mutateAsync` keeps working after this panel unmounts, so
     * the success branch can both leave the archive sentence last in the shell
     * live region and then record the undo source against the committed
     * activity row.
     */
    void archiveCard
      .mutateAsync({ cardId: card.id, listId, title: card.title })
      .then(() => {
        announce(t('quickEdit.archived', { title }));
        void recordSource({
          boardId,
          cardId: card.id,
          scope: { kind: 'card', cardId: card.id },
          eventTypes: ['card.archived'],
          label: t('undo.source.cardArchived', { title }),
        });
      })
      .catch(() => {
        /* `useArchiveCard`'s own `onError` announces the rollback — header. */
      });
    close();
  }

  // Escape closes the whole panel when focus is on Archive/Open card, not
  // just the title field. When focus IS in the field, `InlineEdit`'s own
  // handler calls `stopPropagation`/`stopImmediatePropagation` before this
  // ever runs (see its docstring) and reaches `close()` via `onCancel`
  // instead — so that branch and this one never both fire for one keypress.
  //
  // Enter is the opposite case: `InlineEdit` does NOT stop its propagation
  // (see the file header), so every Enter that reaches the field also
  // reaches here, and `close()` is called unconditionally rather than only
  // for the two commit-less branches — `close()`'s own idempotency guard is
  // what makes that safe on the branch that already closed via a real commit.
  //
  // The parameter is a structural subset of `KeyboardEvent<T>` rather than
  // that type itself: `CardSurface.onKeyDown` types as the INTERSECTION of
  // `div`/`a`/`button` keyboard handlers (the same reason its own `ref`
  // needs a cast — see that file's comment), and only a parameter type every
  // branch of that union satisfies typechecks against all three at once.
  function isActionTarget(target: EventTarget | null): boolean {
    return target instanceof HTMLElement && target.closest('a,button') !== null;
  }

  function handlePanelKeyDown(event: KeyboardEvent<Element>) {
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }
    if (event.key === 'Enter' && !isActionTarget(event.target)) close();
  }

  return (
    <CardSurface
      as="div"
      variant="sheet"
      role="group"
      aria-label={groupLabel}
      onKeyDown={handlePanelKeyDown}
      /*
       * ⚠ `border-border-strong`, not `border-accent`, and the screenshot pass
       * is why. The panel border, `InlineEdit`'s `focus:border-accent` and the
       * global `:focus-visible` outline are three separate strokes of the SAME
       * `--color-accent`, stacked inside about ten pixels — and the result
       * reads as a validation error rather than as a card that has been lifted
       * to be edited. Nothing was wrong in any of the three files; the colour
       * simply meant "focus" twice and "this panel" once.
       *
       * `--color-border-strong` is the control-boundary token (≥3:1) and says
       * exactly what this edge is for, leaving the accent to mean focus alone.
       * Elevation would be the better signal, but `styles.css` has only
       * `--shadow-card` — a second elevation token is a design-system addition
       * and is recorded as such rather than invented here.
       */
      className="relative z-10 flex flex-col gap-2 border border-border-strong p-2"
    >
      <InlineEdit
        value={card.title}
        label={t('quickEdit.titleLabel')}
        onCommit={handleTitleCommit}
        onCancel={close}
        commitOn="enter"
        defaultEditing
        placeholder={t('board.card.untitled')}
        className="text-card leading-[1.35] font-medium text-ink"
      />
      <div className="flex flex-col gap-1">
        <Button
          as="a"
          href={card.path}
          variant="quiet"
          size="sm"
          className="justify-start"
        >
          {t('quickEdit.open')}
        </Button>
        {/*
          `quiet`, not `danger`: `ListHeader.tsx`'s own rule for its list
          archive item applies unchanged here — archiving is reversible
          (`FR-BOARD-028`'s Behaviour: "reversible by restore"), and
          colouring it as destruction would be a lie.
        */}
        {onMove ? (
          <Button
            type="button"
            variant="quiet"
            size="sm"
            className="justify-start"
            onClick={() => {
              onMove();
              close();
            }}
          >
            {t('quickEdit.move')}
          </Button>
        ) : null}
        {onCopy ? (
          <Button
            type="button"
            variant="quiet"
            size="sm"
            className="justify-start"
            onClick={() => {
              onCopy();
              close();
            }}
          >
            {t('quickEdit.copy')}
          </Button>
        ) : null}
        <Button
          type="button"
          variant="quiet"
          size="sm"
          className="justify-start"
          onClick={handleArchive}
        >
          {t('quickEdit.archive')}
        </Button>
      </div>
    </CardSurface>
  );
}
